import { useEffect, type ReactElement } from 'react';
import { Box, SimpleGrid } from '@mantine/core';
import { useViewModel } from '@/shared/viewmodels/useViewModel';
import type { AnalyticsFilter } from '@/shared/analytics/AnalyticsTypes';
import { SurfaceSignalsViewModel } from './SurfaceSignalsViewModel';
import { makeRng } from './SignalSampleData';
import SignalTile from './SignalTile';
import type { SignalTileDef } from './SignalTypes';

interface SignalTileGridProps {
    defs: SignalTileDef[];
    /** Filters handed to every tile's live loader (page assist filters, plus any page-scoped filter). */
    filters: AnalyticsFilter[];
    /** Identity of the global filter state; changing it re-runs the live loaders (they apply globals at the gateway). */
    scopeKey?: string;
    cols?: { base?: number; sm?: number; md?: number; lg?: number; xl?: number };
}

/**
 * Renders an explicit list of signal tiles against a given filter set —
 * the presentational core shared by {@link SurfaceSignalGrid} (a surface's
 * registry tiles under the ambient assist filters) and the spend drill pages
 * (context tiles scoped to a department). Each tile shows a loader while its
 * live data is in flight, then the live result; the deterministic sample is
 * the fallback, shown only if the live loader fails (or the tile has none).
 */
export default function SignalTileGrid({ defs, filters, scopeKey = '', cols }: SignalTileGridProps): ReactElement {
    const vm = useViewModel(SurfaceSignalsViewModel);

    useEffect(() => {
        vm.load(defs, filters, scopeKey);
    }, [vm, defs, filters, scopeKey]);

    return (
        <SimpleGrid cols={cols ?? { base: 1, sm: 2, lg: 3 }} spacing="lg" style={{ alignItems: 'stretch', gridAutoFlow: 'dense' }}>
            {defs.map((def) => (
                <Box key={def.id} style={{ gridColumn: def.span === 2 ? 'span 2' : undefined }}>
                    <SignalTile def={def} data={vm.data[def.id] ?? def.sample(makeRng(def.id))} isLive={vm.isLive(def.id)} loading={vm.isLoading(def.id)} />
                </Box>
            ))}
        </SimpleGrid>
    );
}
